'use client'

import {
  createGraphicFromFile,
  createGraphicFromPrompt,
  fetchGraphic as fetchGraphicApi,
  updateGraphic as updateGraphicApi,
  updateGraphicWithHtml,
} from '@/lib/api'
import { createContext, ReactNode, useContext, useState } from 'react'
import {
  ErrorMessage,
  Graphic,
  GraphicMessage,
  GraphicResult,
  SystemMessage,
  UpdateGraphicMessage,
} from 'sprites-types/api/v2/graphics'
import { v4 as uuidv4 } from 'uuid'

/**
 * The GraphicContext is used to store the state of the graphic object. (No explicit UX/UI state)
 */

interface GraphicContextType {
  graphic: Graphic | null
  isLoading: boolean
  updateGraphicHtml: (html: string) => Promise<GraphicResult>
  createFromFile: (htmlFileUrl: string) => Promise<GraphicResult>
  createFromPrompt: (prompt: string) => Promise<GraphicResult>
  sendMessage: (message: GraphicMessage) => Promise<void>
  fetchGraphic: (id: string) => Promise<GraphicResult>
  undo: () => void
  redo: () => void
  getMessages: () => GraphicMessage[]
  reset: () => void
  hasChanges: () => boolean
  setInitialGraphic: (graphic: Graphic) => void
}

interface GraphicOperation {
  graphic: Graphic

  // This is to keep track of the messages that were part of the change for undo/redo purposes
  // When undo-ing, we need to undo all the messages up to and including startMessageId
  // When redo-ing, we need to redo all the messages up to and including endMessageId
  startMessageId?: string
  endMessageId?: string
  previousMessages?: GraphicMessage[]
}

const GraphicContext = createContext<GraphicContextType | undefined>(undefined)

interface GraphicProviderProps {
  children: ReactNode
}

/*
 * This is the main context for the graphic object, we should manage fetching and updating a specific graphic
 * here. Any UI state data should not be added here.
 */
export function GraphicProvider({ children }: GraphicProviderProps) {
  const [graphic, setGraphic] = useState<Graphic | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [graphicStack, setGraphicStack] = useState<GraphicOperation[]>([])
  const [graphicStackIndex, setGraphicStackIndex] = useState<number>(-1)

  interface MessageState {
    queue: GraphicMessage[]
    index: number
  }

  const initialMessageState: MessageState = {
    queue: [
      {
        id: uuidv4(),
        type: 'system',
        message:
          'Ask Sprites AI to edit your graphic for you! It is best to ask for one change at a time. (ex: "Use a blue color theme instead")',
      } as SystemMessage,
    ],
    index: 0,
  }

  const [messageState, setMessageState] = useState<MessageState>(initialMessageState)

  const pushGraphic = (operation: GraphicOperation) => {
    const newStack = [...graphicStack.slice(0, graphicStackIndex + 1), operation]
    setGraphicStack(newStack)
    setGraphicStackIndex(graphicStackIndex + 1)
    setGraphic(operation.graphic)
  }

  const pushMessage = (message: GraphicMessage) => {
    setMessageState((prev) => {
      const newQueue = [...prev.queue.slice(0, prev.index + 1), message]
      return { queue: newQueue, index: prev.index + 1 }
    })
  }

  const setMessageIndex = (newIndex: number) => {
    setMessageState((prev) => ({
      queue: prev.queue,
      index: newIndex,
    }))
  }

  const redo = () => {
    if (isLoading) {
      return
    }

    if (graphicStackIndex < graphicStack.length - 1) {
      const newIndex = graphicStackIndex + 1
      setGraphicStackIndex(newIndex)
      setGraphic(graphicStack[newIndex].graphic)
      const messageId = graphicStack[newIndex].endMessageId
      if (messageId) {
        const index = messageState.queue.findIndex((m) => m.id === messageId)
        if (index !== -1) {
          setMessageIndex(index)
        }
      }
    }
  }

  const undo = () => {
    if (isLoading) {
      return
    }

    if (graphicStackIndex > 0) {
      const messageId = graphicStack[graphicStackIndex].startMessageId

      const newIndex = graphicStackIndex - 1
      setGraphicStackIndex(newIndex)
      setGraphic(graphicStack[newIndex].graphic)

      if (messageId) {
        const index = messageState.queue.findIndex((m) => m.id === messageId)
        setMessageIndex(index === -1 ? -1 : index - 1)
      }
    }
  }

  const sendMessage = async (message: GraphicMessage) => {
    const previousMessages = getMessages()

    pushMessage(message)

    if (message.type === 'update_graphic') {
      const updatedGraphic = await updateGraphic(message as UpdateGraphicMessage, previousMessages)

      if (updatedGraphic.success) {
        const endMessageId = uuidv4()
        pushMessage({
          id: endMessageId,
          type: 'system',
          message: updatedGraphic.summary || 'Changes made',
        } as SystemMessage)

        pushGraphic({
          graphic: updatedGraphic.graphic!,
          startMessageId: message.id,
          endMessageId: endMessageId,
          previousMessages,
        })
      } else {
        pushMessage({
          id: uuidv4(),
          type: 'error',
          message: updatedGraphic.error,
        } as ErrorMessage)
      }
    }
  }

  const updateGraphicHtml = async (html: string): Promise<GraphicResult> => {
    if (!graphic) {
      console.error('No graphic to update')
      return { success: false, error: 'No graphic to update' }
    }

    if (isLoading) {
      return { success: false, error: 'Update already in progress' }
    }

    setIsLoading(true)

    const result = await updateGraphicWithHtml(graphic.id, html)

    setIsLoading(false)

    if (!result.success) {
      console.error('Failed to update graphic:', result.error)
      return { success: false, error: result.error || 'Failed to update graphic' }
    }

    pushGraphic({ graphic: result.graphic })

    return { success: true, graphic: result.graphic }
  }

  const createFromFile = async (htmlFileUrl: string): Promise<GraphicResult> => {
    if (isLoading) {
      return { success: false, error: 'Creation already in progress' }
    }

    setIsLoading(true)
    const result = await createGraphicFromFile(htmlFileUrl)

    setIsLoading(false)

    if (result.success) {
      pushGraphic({ graphic: result.graphic })
      return { success: true, graphic: result.graphic }
    }

    return { success: false, error: result.error || 'Failed to create graphic' }
  }

  const createFromPrompt = async (prompt: string): Promise<GraphicResult> => {
    if (isLoading) {
      return { success: false, error: 'Creation already in progress' }
    }

    setIsLoading(true)
    const result = await createGraphicFromPrompt(prompt.trim())

    setIsLoading(false)

    if (result.success) {
      pushGraphic({ graphic: result.graphic })
      return { success: true, graphic: result.graphic }
    }
    return { success: false, error: result.error || 'Failed to create graphic' }
  }

  const updateGraphic = async (
    message: UpdateGraphicMessage,
    previousMessages?: GraphicMessage[]
  ): Promise<GraphicResult> => {
    if (!graphic) {
      return { success: false, error: 'No graphic to update' }
    }

    if (isLoading) {
      return { success: false, error: 'Update already in progress' }
    }

    setIsLoading(true)
    const result = await updateGraphicApi(graphic.id, message, previousMessages)

    setIsLoading(false)

    if (result.success) {
      return { success: true, graphic: result.graphic, summary: result.summary }
    }

    return { success: false, error: result.error || 'Failed to update graphic' }
  }

  const fetchGraphic = async (id: string): Promise<GraphicResult> => {
    if (isLoading) {
      return { success: false, error: 'Fetch already in progress' }
    }

    if (graphic?.id === id) {
      return { success: true, graphic }
    }

    if (!id) {
      return { success: false, error: 'No graphic ID provided' }
    }

    setIsLoading(true)
    const result = await fetchGraphicApi(id)

    setIsLoading(false)

    if (result.success) {
      setInitialGraphic(result.graphic)
      return { success: true, graphic: result.graphic }
    }

    return { success: false, error: result.error || 'Failed to fetch graphic' }
  }

  const reset = () => {
    setGraphic(null)
    setIsLoading(false)
    setGraphicStack([])
    setGraphicStackIndex(-1)
    setMessageState(initialMessageState)
  }

  const getMessages = (): GraphicMessage[] => {
    return messageState.queue.slice(0, messageState.index + 1)
  }

  const hasChanges = (): boolean => {
    return graphicStackIndex > 0
  }

  const setInitialGraphic = (newGraphic: Graphic) => {
    reset()
    pushGraphic({ graphic: newGraphic })
  }

  const contextValue: GraphicContextType = {
    graphic,
    isLoading,
    updateGraphicHtml,
    createFromFile,
    createFromPrompt,
    sendMessage,
    fetchGraphic,
    undo,
    redo,
    getMessages,
    reset,
    hasChanges,
    setInitialGraphic,
  }

  return <GraphicContext.Provider value={contextValue}>{children}</GraphicContext.Provider>
}

export function useGraphic() {
  const context = useContext(GraphicContext)
  if (context === undefined) {
    throw new Error('useGraphic must be used within a GraphicProvider')
  }
  return context
}

export type { Graphic, GraphicContextType }
